import { useState, useEffect } from 'react';
import { Plus, Trash2, Power, PowerOff, Clock, Edit2, X } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface Schedule {
  id: string;
  name: string;
  campaign_type: string;
  frequency: string;
  day_of_week: number | null;
  day_of_month: number | null;
  send_hour: number;
  timezone: string;
  is_active: boolean;
  ai_provider: string;
  prompt_context: string | null;
  category_id: string | null;
  category_name: string | null;
  default_layout: string;
  target_tags: string[];
  last_generated_at: string | null;
  last_sent_at: string | null;
  created_at: string;
}

const LAYOUT_OPTIONS = [
  { label: 'Classic', value: 'classic' },
  { label: 'Product Grid', value: 'product_grid' },
  { label: 'Single Spotlight', value: 'single_spotlight' },
  { label: 'Seasonal Guide', value: 'seasonal_guide' },
  { label: 'Minimal', value: 'minimal' },
];

const SUGGESTED_TAGS: string[] = [];

const CAMPAIGN_TYPES = [
  { label: 'New Arrivals', value: 'new_arrivals' },
  { label: 'Seasonal Guide', value: 'seasonal_planting' },
  { label: 'Featured Products', value: 'featured_products' },
  { label: 'Category Spotlight', value: 'category_spotlight' },
  { label: 'Tips & Guides', value: 'growing_tips' },
  { label: 'Promotions & Sales', value: 'promotions' },
  { label: 'Custom', value: 'custom' },
];

const FREQUENCIES = [
  { label: 'Weekly', value: 'weekly' },
  { label: 'Biweekly', value: 'biweekly' },
  { label: 'Monthly', value: 'monthly' },
];

const DAYS_OF_WEEK = [
  { label: 'Sunday', value: '0' },
  { label: 'Monday', value: '1' },
  { label: 'Tuesday', value: '2' },
  { label: 'Wednesday', value: '3' },
  { label: 'Thursday', value: '4' },
  { label: 'Friday', value: '5' },
  { label: 'Saturday', value: '6' },
];

const PROVIDERS = [
  { label: 'Google (Gemini)', value: 'google' },
  { label: 'HuggingFace', value: 'huggingface' },
  { label: 'Pollinations', value: 'pollinations' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  label: `${i === 0 ? '12' : i > 12 ? i - 12 : i}:00 ${i < 12 ? 'AM' : 'PM'}`,
  value: i.toString(),
}));

const campaignTypeLabels: Record<string, string> = Object.fromEntries(
  CAMPAIGN_TYPES.map(({ value, label }) => [value, label])
);

interface FormData {
  name: string;
  campaign_type: string;
  frequency: string;
  day_of_week: string;
  day_of_month: string;
  send_hour: string;
  timezone: string;
  ai_provider: string;
  prompt_context: string;
  default_layout: string;
  target_tags: string[];
}

const defaultForm: FormData = {
  name: '',
  campaign_type: 'seasonal_planting',
  frequency: 'weekly',
  day_of_week: '2', // Tuesday
  day_of_month: '1',
  send_hour: '10',
  timezone: 'America/New_York',
  ai_provider: 'google',
  prompt_context: '',
  default_layout: 'classic',
  target_tags: [],
};

export default function NewsletterSchedules() {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>(defaultForm);
  const [isSaving, setIsSaving] = useState(false);

  const fetchSchedules = async () => {
    setIsLoading(true);
    try {
      const res = await api.get<{ schedules: Schedule[] }>('/admin/newsletter/schedules');
      setSchedules(res.schedules || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load schedules');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchSchedules(); }, []);

  const handleToggle = async (id: string) => {
    try {
      const updated = await api.patch<Schedule>(`/admin/newsletter/schedules/${id}/toggle`);
      setSchedules(s => s.map(sc => sc.id === id ? { ...sc, is_active: updated.is_active } : sc));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle schedule');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this schedule? This will not delete any campaigns already generated from it.')) return;
    try {
      await api.delete(`/admin/newsletter/schedules/${id}`);
      setSchedules(s => s.filter(sc => sc.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete schedule');
    }
  };

  const handleEdit = (schedule: Schedule) => {
    setEditingId(schedule.id);
    setForm({
      name: schedule.name,
      campaign_type: schedule.campaign_type,
      frequency: schedule.frequency,
      day_of_week: schedule.day_of_week?.toString() || '2',
      day_of_month: schedule.day_of_month?.toString() || '1',
      send_hour: schedule.send_hour.toString(),
      timezone: schedule.timezone,
      ai_provider: schedule.ai_provider,
      prompt_context: schedule.prompt_context || '',
      default_layout: schedule.default_layout || 'classic',
      target_tags: schedule.target_tags || [],
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const payload = {
        name: form.name,
        campaign_type: form.campaign_type,
        frequency: form.frequency,
        day_of_week: ['weekly', 'biweekly'].includes(form.frequency) ? parseInt(form.day_of_week) : undefined,
        day_of_month: form.frequency === 'monthly' ? parseInt(form.day_of_month) : undefined,
        send_hour: parseInt(form.send_hour),
        timezone: form.timezone,
        ai_provider: form.ai_provider,
        prompt_context: form.prompt_context || undefined,
        default_layout: form.default_layout,
        target_tags: form.target_tags.length > 0 ? form.target_tags : undefined,
      };

      if (editingId) {
        const updated = await api.put<Schedule>(`/admin/newsletter/schedules/${editingId}`, payload);
        setSchedules(s => s.map(sc => sc.id === editingId ? { ...sc, ...updated } : sc));
      } else {
        const created = await api.post<Schedule>('/admin/newsletter/schedules', payload);
        setSchedules(s => [created, ...s]);
      }

      setShowForm(false);
      setEditingId(null);
      setForm(defaultForm);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save schedule');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(defaultForm);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Email Schedules</h1>
          <p className="mt-2 text-sm text-gray-600">
            Set up recurring AI-generated email campaigns
          </p>
        </div>
        {!showForm && (
          <Button onClick={() => { setShowForm(true); setEditingId(null); setForm(defaultForm); }}>
            <Plus className="h-4 w-4 mr-2" />
            New Schedule
          </Button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg" role="alert">
          {error}
          <button onClick={() => setError(null)} className="float-right">&times;</button>
        </div>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <Card padding="lg">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {editingId ? 'Edit Schedule' : 'New Schedule'}
            </h2>
            <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Weekly Newsletter"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Type</label>
                <Select
                  value={form.campaign_type}
                  onChange={(e) => setForm({ ...form, campaign_type: e.target.value })}
                  options={CAMPAIGN_TYPES}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                <Select
                  value={form.frequency}
                  onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                  options={FREQUENCIES}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">AI Provider</label>
                <Select
                  value={form.ai_provider}
                  onChange={(e) => setForm({ ...form, ai_provider: e.target.value })}
                  options={PROVIDERS}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['weekly', 'biweekly'].includes(form.frequency) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Day of Week</label>
                  <Select
                    value={form.day_of_week}
                    onChange={(e) => setForm({ ...form, day_of_week: e.target.value })}
                    options={DAYS_OF_WEEK}
                  />
                </div>
              )}
              {form.frequency === 'monthly' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Day of Month</label>
                  <Select
                    value={form.day_of_month}
                    onChange={(e) => setForm({ ...form, day_of_month: e.target.value })}
                    options={Array.from({ length: 28 }, (_, i) => ({
                      label: (i + 1).toString(),
                      value: (i + 1).toString(),
                    }))}
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Send Time</label>
                <Select
                  value={form.send_hour}
                  onChange={(e) => setForm({ ...form, send_hour: e.target.value })}
                  options={HOURS}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                AI Prompt Context (optional)
              </label>
              <textarea
                value={form.prompt_context}
                onChange={(e) => setForm({ ...form, prompt_context: e.target.value })}
                placeholder="e.g., Highlight bestsellers, mention promotions..."
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Default Layout</label>
                <Select
                  value={form.default_layout}
                  onChange={(e) => setForm({ ...form, default_layout: e.target.value })}
                  options={LAYOUT_OPTIONS}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Subscriber Tags</label>
                <div className="flex flex-wrap gap-1.5">
                  {SUGGESTED_TAGS.map(tag => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => setForm(f => ({
                        ...f,
                        target_tags: f.target_tags.includes(tag)
                          ? f.target_tags.filter(t => t !== tag)
                          : [...f.target_tags, tag],
                      }))}
                      className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                        form.target_tags.includes(tag)
                          ? 'bg-green-100 border-green-300 text-green-800'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
                {form.target_tags.length === 0 && (
                  <p className="text-xs text-gray-400 mt-1">No tags = send to all subscribers</p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : editingId ? 'Update' : 'Create Schedule'}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Schedules List */}
      {isLoading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : schedules.length === 0 && !showForm ? (
        <Card padding="lg">
          <div className="text-center py-8 text-gray-500">
            <Clock className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No schedules yet</p>
            <p className="text-sm mt-1">Create a schedule to automate your newsletter campaigns</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {schedules.map((schedule) => (
            <Card key={schedule.id} padding="md">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold text-gray-900">{schedule.name}</h3>
                    <Badge variant={schedule.is_active ? 'green' : 'gray'}>
                      {schedule.is_active ? 'Active' : 'Paused'}
                    </Badge>
                    <Badge variant="blue">{campaignTypeLabels[schedule.campaign_type] || schedule.campaign_type}</Badge>
                  </div>
                  <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                    <span className="capitalize">{schedule.frequency}</span>
                    <span>
                      {['weekly', 'biweekly'].includes(schedule.frequency) && schedule.day_of_week != null
                        ? DAYS_OF_WEEK.find(d => d.value === schedule.day_of_week?.toString())?.label
                        : schedule.frequency === 'monthly' && schedule.day_of_month
                          ? `Day ${schedule.day_of_month}`
                          : ''}
                    </span>
                    <span>at {HOURS.find(h => h.value === schedule.send_hour.toString())?.label}</span>
                    {schedule.last_sent_at && (
                      <span>Last sent: {new Date(schedule.last_sent_at).toLocaleDateString()}</span>
                    )}
                  </div>
                  {schedule.default_layout && schedule.default_layout !== 'classic' && (
                    <Badge variant="gray">{LAYOUT_OPTIONS.find(l => l.value === schedule.default_layout)?.label || schedule.default_layout}</Badge>
                  )}
                  {schedule.target_tags?.length > 0 && (
                    <div className="mt-1 flex gap-1">
                      {schedule.target_tags.map(tag => (
                        <span key={tag} className="px-1.5 py-0.5 text-xs bg-green-50 text-green-700 rounded">{tag}</span>
                      ))}
                    </div>
                  )}
                  {schedule.prompt_context && (
                    <p className="mt-1 text-xs text-gray-400 truncate max-w-lg">{schedule.prompt_context}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggle(schedule.id)}
                    className={`p-2 rounded-lg transition-colors ${
                      schedule.is_active
                        ? 'text-green-600 hover:bg-green-50'
                        : 'text-gray-400 hover:bg-gray-100'
                    }`}
                    title={schedule.is_active ? 'Pause' : 'Activate'}
                  >
                    {schedule.is_active ? <Power className="h-4 w-4" /> : <PowerOff className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => handleEdit(schedule)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(schedule.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
